import React from 'react';
import { Card, Icon, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { Build, CheckCircle } from '@mui/icons-material';
import { PrometheusTab } from '../../../../types/generic';

interface MaintenanceTabSelectProps {
	name: string;
	path: string;
	disabledTabs: string[];
	selectedTabs: string[];
	onChangeSelected: (path: string) => void;
	childTabs?: PrometheusTab[];
}

const MaintenanceTabGroup: React.FC<MaintenanceTabSelectProps> = ({
	name,
	path,
	disabledTabs,
	selectedTabs,
	onChangeSelected,
	childTabs,
}) => {
	const enabled = Boolean(disabledTabs.includes(path) || disabledTabs.includes(path.split('.')[0]) || disabledTabs.includes('All'));
	if (path === 'Admin.System') {
		return (
			<Tooltip title="You cannot disable this tab.">
				<Stack
					direction="row"
					alignItems="center"
					component={Card}
					variant="elevation"
					sx={{ padding: '5px', pl: '10px', br: '10px' }}
					gap={1}
				>
					<Icon color={enabled ? 'warning' : 'success'}>{enabled ? <Build /> : <CheckCircle />}</Icon>
					<Typography id={`${name}-switch-label`}>{name}</Typography>
					<Switch
						disabled
						sx={{ ml: 'auto' }}
						checked={false}
						slotProps={{ input: { 'aria-labelledby': `${name}-switch-label`, 'aria-label': `${name}-maintenance-switch` } }}
						data-testid={`${name}-maintenance-switch`}
					/>
				</Stack>
			</Tooltip>
		);
	}
	return (
		<Stack direction="column" component={Card} variant="outlined" gap={0}>
			<Stack direction="row" alignItems="center" sx={{ padding: '5px', pl: '10px', br: '10px' }} gap={1}>
				<Tooltip title={enabled ? 'This tab is currently disabled' : 'This tab is currently available to users'}>
					<Icon sx={{ position: 'relative', top: '-2px' }} color={enabled ? 'warning' : 'success'}>
						{enabled ? <Build /> : <CheckCircle />}
					</Icon>
				</Tooltip>
				<Typography id={`${name}-switch-label`}>{name}</Typography>
				<Switch
					sx={{ ml: 'auto' }}
					checked={selectedTabs.includes(name) || selectedTabs.includes(path) || selectedTabs.includes('All')}
					onChange={() => onChangeSelected(path)}
					slotProps={{ input: { 'aria-labelledby': `${name}-switch-label`, 'aria-label': `${name}-maintenance-switch` } }}
					data-testid={`${name}-maintenance-switch`}
				/>
			</Stack>
			{childTabs?.map((tab) => (
				<Card key={tab.name} sx={{ m: 0.25 }}>
					<Stack direction="row" sx={{ padding: '5px', pl: '10px', br: '10px', mb: '2px' }} alignItems="center" gap={1}>
						<Tooltip title={enabled ? 'This tab is currently disabled' : 'This tab is currently available to users'}>
							<Icon sx={{ position: 'relative', top: '-2px' }} color={enabled ? 'warning' : 'success'}>
								{enabled ? <Build /> : <CheckCircle />}
							</Icon>
						</Tooltip>
						<Typography id={`${tab.name}-switch-label`}>{tab.name.concat(' tab')}</Typography>
						<Switch
							sx={{ ml: 'auto' }}
							checked={
								selectedTabs.includes(path.concat('.').concat(tab.name)) ||
								selectedTabs.includes(path) ||
								(tab.name !== 'System' && selectedTabs.includes('All'))
							}
							onChange={() => onChangeSelected(path.concat('.').concat(tab.name))}
							slotProps={{ input: { 'aria-labelledby': `${name}-switch-label`, 'aria-label': `${tab.name}-maintenance-switch` } }}
							data-testid={`${tab.name}-maintenance-switch`}
							disabled={tab.name === 'System'}
						/>
					</Stack>
				</Card>
			))}
		</Stack>
	);
};

export default MaintenanceTabGroup;
